import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, FolderKanban, ArrowUpRight, List, LayoutGrid, ArrowUp, ArrowDown, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import ProjectTasksPopup from "@/components/Client/ProjectTasksPopup";

const HEALTH_LABEL = { on_track: "On track", at_risk: "At risk",
                       off_track: "Off track", blocked: "Blocked" };
const HEALTH_COLOR = {
  on_track: "text-emerald-700 border-emerald-200 bg-emerald-50",
  at_risk: "text-amber-700 border-amber-200 bg-amber-50",
  off_track: "text-rose-700 border-rose-200 bg-rose-50",
  blocked: "text-rose-800 border-rose-300 bg-rose-100",
};

function shortDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return s; }
}

function sortHeadRender({ label, k, align, sortKey, sortDir, toggleSort }) {
  return (
    <th className={`p-2 border-b border-border cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"}`}
        onClick={() => toggleSort(k)}
        data-testid={`cpt-sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}

export default function ClientProjectsTab({ clientId, projects, onCreated }) {
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const [tasksFor, setTasksFor] = useState(null);   // R148e · quick tasks popup target
  const [form, setForm] = useState({ name: "", description: "", brand_id: "" });
  const [busy, setBusy] = useState(false);
  const [brands, setBrands] = useState([]);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(`clientprojects.view.${clientId}`) || "list"; }
    catch { return "list"; }
  });
  const [enriched, setEnriched] = useState({});
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem(`clientprojects.view.${clientId}`, v); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open || !clientId) return;
    api.get(`/clients/${clientId}/brands`)
      .then(r => setBrands((r.data && (r.data.rows || r.data)) || []))
      .catch(() => setBrands([]));
  }, [open, clientId]);

  // Fetch per-project task counts for the list view
  useEffect(() => {
    if (view !== "list" || !projects || projects.length === 0 || !activeWorkspaceId) return;
    let cancelled = false;
    const load = async () => {
      const acc = {};
      await Promise.all(projects.map(async (p) => {
        try {
          const { data } = await api.get(
            `/workspaces/${activeWorkspaceId}/tasks-search`,
            { params: { project_id: p.id } });
          const total = (data || []).length;
          const open = (data || []).filter((t) => t.status !== "done").length;
          const done = total - open;
          const pct = total ? Math.round((done / total) * 100) : 0;
          acc[p.id] = { total, open, done, pct };
        } catch { acc[p.id] = { total: 0, open: 0, done: 0, pct: 0 }; }
      }));
      if (!cancelled) setEnriched(acc);
    };
    load();
    return () => { cancelled = true; };
  }, [view, projects, activeWorkspaceId]);

  const sortedProjects = useMemo(() => {
    const arr = [...(projects || [])];
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === "name") { va = a.name || ""; vb = b.name || ""; }
      else if (sortKey === "health") { va = a.health || "z"; vb = b.health || "z"; }
      else if (sortKey === "status") { va = a.status || "z"; vb = b.status || "z"; }
      else if (sortKey === "start") { va = a.start_date || ""; vb = b.start_date || ""; }
      else if (sortKey === "end") { va = a.end_date || a.due_date || ""; vb = b.end_date || b.due_date || ""; }
      else if (sortKey === "pct") { va = enriched[a.id]?.pct ?? -1; vb = enriched[b.id]?.pct ?? -1; }
      else if (sortKey === "open") { va = enriched[a.id]?.open ?? -1; vb = enriched[b.id]?.open ?? -1; }
      else if (sortKey === "created") { va = a.created_at || ""; vb = b.created_at || ""; }
      else { va = ""; vb = ""; }
      const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [projects, sortKey, sortDir, enriched]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortHead = ({ label, k, align = "left" }) => sortHeadRender({ label, k, align, sortKey, sortDir, toggleSort });

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, client_id: clientId };
      if (!payload.brand_id) delete payload.brand_id;
      const { data } = await api.post(`/clients/${clientId}/projects`, payload);
      setOpen(false);
      setForm({ name: "", description: "", brand_id: "" });
      toast.success("Project created");
      onCreated && onCreated();
      nav(`/app/projects/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to create project"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">{projects.length} project{projects.length === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-border rounded-none" data-testid="cpt-view-switch">
            <button type="button"
                    className={`h-8 px-3 text-xs inline-flex items-center gap-1 ${view === "list" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                    onClick={() => setViewPersist("list")}
                    data-testid="cpt-view-list">
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button type="button"
                    className={`h-8 px-3 text-xs inline-flex items-center gap-1 border-l border-border ${view === "cards" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                    onClick={() => setViewPersist("cards")}
                    data-testid="cpt-view-cards">
              <LayoutGrid className="w-3.5 h-3.5" /> Cards
            </button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none" data-testid="client-new-project-btn"><Plus className="w-4 h-4 mr-2" />New project</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create project</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-form-name" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-form-desc" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Brand (optional)</Label>
                  <select value={form.brand_id}
                              onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                              className="h-9 w-full border border-border rounded-none bg-background text-sm px-2"
                              data-testid="project-form-brand">
                    <option value="">— no brand —</option>
                    {brands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Link this project to a brand to make it appear on that brand&apos;s Projects tab.
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-none" disabled={busy} data-testid="project-form-submit">{busy ? "Creating…" : "Create"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Create your first project for this client."
          icon={FolderKanban}
          testid="client-detail-empty"
          action={<Button className="rounded-none" onClick={() => setOpen(true)} data-testid="client-empty-create-btn">Create project</Button>}
        />
      ) : view === "list" ? (
        <div className="border border-border overflow-x-auto" data-testid="cpt-list">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-muted/40">
              <tr>
                <SortHead label="Project" k="name" />
                <SortHead label="Health" k="health" />
                <SortHead label="Status" k="status" />
                <SortHead label="Complete %" k="pct" align="right" />
                <SortHead label="Open / Total" k="open" align="right" />
                <SortHead label="Start" k="start" />
                <SortHead label="Due / End" k="end" />
                <SortHead label="Created" k="created" />
                <th className="p-2 border-b border-border w-16 text-right">Tasks</th>
                <th className="p-2 border-b border-border w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sortedProjects.map((p) => {
                const e = enriched[p.id];
                return (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/20 cursor-pointer"
                      onClick={() => nav(`/app/projects/${p.id}`)}
                      data-testid={`cpt-row-${p.id}`}>
                    <td className="p-2 font-medium">
                      <div className="flex items-center gap-2">
                        <FolderKanban className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="truncate">{p.name}</span>
                      </div>
                    </td>
                    <td className="p-2">
                      {p.health ? (
                        <span className={`text-[10px] px-2 py-0.5 border ${HEALTH_COLOR[p.health] || ""}`}>
                          {HEALTH_LABEL[p.health] || p.health}
                        </span>
                      ) : <span className="text-[11px] text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">
                      {p.status || "—"}
                    </td>
                    <td className="p-2 text-right">
                      {e ? (
                        <div className="inline-flex items-center gap-2 justify-end min-w-[120px]">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${e.pct}%` }} />
                          </div>
                          <span className="text-xs tabular-nums font-medium w-9 text-right">{e.pct}%</span>
                        </div>
                      ) : <span className="text-[11px] text-muted-foreground">…</span>}
                    </td>
                    <td className="p-2 text-right tabular-nums text-xs">
                      {e ? (
                        <span>
                          <span className={e.open ? "text-amber-700 font-bold" : "text-muted-foreground"}>{e.open}</span>
                          <span className="text-muted-foreground"> / {e.total}</span>
                        </span>
                      ) : "…"}
                    </td>
                    <td className="p-2 text-xs">{shortDate(p.start_date)}</td>
                    <td className="p-2 text-xs">{shortDate(p.end_date || p.due_date)}</td>
                    <td className="p-2 text-xs text-muted-foreground">{fmtRelative(p.created_at)}</td>
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        onClick={(e2) => { e2.stopPropagation(); setTasksFor(p); }}
                        className="inline-flex items-center gap-1 border border-border hover:bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary"
                        title="Quick-view tasks for this project"
                        data-testid={`cpt-tasks-btn-${p.id}`}
                      >
                        <ListChecks className="w-3 h-3" />
                        Tasks
                      </button>
                    </td>
                    <td className="p-2 text-right">
                      <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => (
            <Link key={p.id} to={`/app/projects/${p.id}`} className="border border-border p-6 hover:border-primary/40 group transition" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between mb-4">
                <FolderKanban className="w-5 h-5 text-primary" />
                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "var(--font-heading)" }}>{p.name}</h3>
              <div className="text-xs text-muted-foreground mb-2">created {fmtRelative(p.created_at)}</div>
              {p.description && <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
            </Link>
          ))}
        </div>
      )}

      {tasksFor && (
        <ProjectTasksPopup
          project={tasksFor}
          wsId={activeWorkspaceId}
          onClose={() => setTasksFor(null)}
        />
      )}
    </>
  );
}
